import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';

import { useTranslation } from '../../localization/useTranslation';
import SimpleTable, { createDefaultActions, SortDirection } from '../Common/SimpleTable/SimpleTable';
import ConfirmDeleteModal from '../Modals/ConfirmDeleteModal';
import ContentPage from '../ContentView/ContentPage/ContentPage';
import Tl from '../../localization/LocalizationKeys';
import useDashboardsQuery, { useDeleteDashboard } from './useDashboardsQuery';
import { Icons } from 'components/Common';
import { NodeType } from 'utils/types';
import MultiLabelGroup from 'components/Common/MultiLabelGroup/MultiLabelGroup';
import { sortLocale } from 'utils/string';
import useUpdatedNodes from 'utils/hooks/useUpdatedNodes';
import AlertContext from 'services/AlertContext';

const DefaultSortBy = {
  path: 'displayName',
  direction: SortDirection.ASC
};

const NodeAndAssetLabels = ({ nodesAndAssets, assetNodeMap, isLoading }) => {
  const T = useTranslation();
  const [nodes, assets] = useMemo(() => {
    return [
      sortLocale(_.reduce(nodesAndAssets, (acc, asset) => {
        if (asset.type === NodeType.Node) {
          acc.push({
            // If we don't have a quick fallback name then use query loading state
            isLoading: isLoading && asset.displayName == null,
            label: assetNodeMap[asset.id]?.name ?? assetNodeMap[asset.id]?.displayName ?? asset.displayName ?? T.common.noname,
            color: 'blue'
          });
        }
        return acc;
      }, []), 'label'),
      sortLocale(_.reduce(nodesAndAssets, (acc, asset) => {
        if (asset.type === NodeType.Asset) {
          acc.push({
            // If we don't have a quick fallback name then use query loading state
            isLoading: isLoading && asset.displayName == null,
            label: assetNodeMap[asset.id]?.displayName ?? asset.displayName ?? T.common.noname,
            color: 'green'
          });
        }
        return acc;
      }, []), 'label')
    ];
  }, [nodesAndAssets, isLoading, assetNodeMap, T.common.noname]);
  return <MultiLabelGroup labels={[...nodes, ...assets]}/>;
};

/**
 * Creates a asset and node map for all the valid assets and nodes in the dashboards list
 * @param {Array} dashboards
 * @returns {Object} { isLoading: boolean, assetNodeMap: Object }
 */
const useDashboardAssetMap = (dashboards) => {
  const nodesAndAssets = useMemo(() => _.flatMap(dashboards, 'assets'), [dashboards]);
  const updatedNodes = useUpdatedNodes(nodesAndAssets);

  return useMemo(() => {
    const assetNodeMap = _.keyBy(_.compact(updatedNodes.nodes), 'id');
    return { isLoading: updatedNodes.isLoading, assetNodeMap };
  }, [updatedNodes.isLoading, updatedNodes.nodes]);
};


const DashboardsPage = ({ breadcrumbs }) => {
  const history = useHistory();
  const T = useTranslation();

  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const { addSuccessAlert, addErrorAlert } = useContext(AlertContext);

  const dashboardsQuery = useDashboardsQuery();

  const dashboards = dashboardsQuery.data;

  const deleteDashboard = useDeleteDashboard();
  const onConfirmedDeleteDashboard = useCallback(({ id }) =>{
    deleteDashboard.mutate(id, {
      onSuccess: () => {
        addSuccessAlert(T.common.request.delete.success);
        setConfirmDeleteItem(null);
      },
      onError: () => {
        addErrorAlert(T.common.request.delete.failed);
      }
    });
  }, [T.common.request.delete.failed, T.common.request.delete.success, addErrorAlert, addSuccessAlert, deleteDashboard]);

  const onRowClick = useCallback((dashboard) => {
    history.push(`/admin/dashboards/${dashboard.id}`);
  }, [history]);

  const createDashboard = useCallback(() => {
    history.push('/admin/dashboards/new-dashboard');
  }, [history]);

  const onDelete = useCallback(dashboard => setConfirmDeleteItem(dashboard), []);

  const cancelDelete = useCallback(() => setConfirmDeleteItem(null), []);

  const actions = useMemo(() => createDefaultActions({ onDelete }), [onDelete]);

  const dashboardAssetMap = useDashboardAssetMap(dashboards);

  const columns = useMemo(() => [
    {
      label: Tl.common.name,
      path: 'displayName',
      isSortable: true,
      searchItem: true
    },
    {
      label: Tl.common.model,
      path: 'data.models',
      searchItem: true,
      render: (models) => {
        const labels = sortLocale((models ?? []).map((model) => ({ label: model })), 'label');
        return <MultiLabelGroup labels={labels} />;
      }
    },
    {
      label: Tl.common.assets,
      path: 'data.assets',
      searchItem: true,
      render: (nodesAndAssets) => {
        return (<NodeAndAssetLabels
          nodesAndAssets={nodesAndAssets}
          assetNodeMap={dashboardAssetMap.assetNodeMap}
          isLoading={dashboardAssetMap.isLoading} />);
      }
    },
    {
      label: Tl.dashboard.usingdynamicassets,
      path: 'data.hasDynamicAssets',
      searchItem: true,
      render: (boolString) => boolString && <Icons.Checkmark />
    }
  ], [dashboardAssetMap]);
  const globalActions = useMemo(() => [
    { onClick: createDashboard },
    { onClick: () => history.push('/admin/dashboards/home'),
      label: 'Home Dashboards',
      variant: 'secondary' }
  ], [createDashboard, history]);
  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/dashboards/list'}>
      <SimpleTable
        actions={actions}
        isLoading={dashboardsQuery.isLoading && _.isEmpty(dashboards)}
        onRowClick={onRowClick}
        data={dashboards}
        columns={columns}
        sortBy={DefaultSortBy}
        hasError={dashboardsQuery.isError}
        errorText={T.dashboardspage.error.failedload}
        globalActions={globalActions}
        compact
        usePaging
      />
      <ConfirmDeleteModal
        isLoading={deleteDashboard.isLoading}
        onConfirm={onConfirmedDeleteDashboard}
        item={confirmDeleteItem}
        itemName={confirmDeleteItem?.displayName ?? confirmDeleteItem?.name ?? T.common.noname}
        onCancel={cancelDelete}
      />
    </ContentPage>
  );
};

export default DashboardsPage;
